import { Box, Heading } from 'grommet'
import styled, { css } from 'styled-components'

import OrangeHexagon from '@public/images/emptyHexagon.svg'
import GrayGradientHexagon from '@public/images/gradientGrayPolygon.svg'
import { theme } from '@theme'

import {
  AdditionalImageProp,
  ItemsProps,
  IconProps,
  ContentProps,
  TitleProps,
  ExperienceSectionContainerProps,
} from './types'

const MAX_WIDTH_LAYOUT = 998

const svgClassesStyles = (imageClassName: string) => `
    path.${imageClassName}_svg__white{
      fill: ${theme.newColors.white};
      transition: .3s all ease;
    }
    path.${imageClassName}_svg__whiteStroke{
      stroke: ${theme.newColors.white};
      transition: .3s all ease;
    }
    path.${imageClassName}_svg__lightOrange{
      stroke: ${theme.newColors.lightOrange};
      transition: .3s all ease;
    }
    path.${imageClassName}_svg__secondary{
      fill: url(#${imageClassName}_svg__secondary);
      transition: .3s all ease;
    }
    path.${imageClassName}_svg__orangeGradient{
      fill: url(#${imageClassName}_svg__orangeGradient);
      transition: .3s all ease;
    }
    rect.${imageClassName}_svg__white-figure{
      fill: ${theme.newColors.white};
      transition: .3s all ease;
    };
    path.${imageClassName}_svg__gradientGray{
      fill: url(#${imageClassName}_svg__gradientGray);
      transition: .3s all ease;
    };
    path.${imageClassName}_svg__gradientDarkGray{
      fill: url(#${imageClassName}_svg__gradientDarkGray);
    };
    path.${imageClassName}_svg__lightGray{
      fill: ${theme.newColors.lightGray};
      transition: .3s all ease;
    };
  `

export const ExperienceSectionContainer = styled.div<ExperienceSectionContainerProps>`
  ${({ theme, padding }) => css`
    margin: 0 auto;
    max-width: ${MAX_WIDTH_LAYOUT}px;
    position: relative;
    z-index: 1;
    .description-title {
      text-transform: initial;

      ${theme.below.tablet`
        ${theme.getTypography('mobile', 'title', 'title1')}

        line-height: 34px;
      `}
    }
    .description-subtitle {
      padding: 0px ${theme.newSpaces.xs2}px;

      ${theme.below.tablet`
        ${theme.getTypography('mobile', 'body', 'body2')}

        & > a {
          ${theme.getTypography('mobile', 'body', 'body2')}
        }
      `}
    }
    ${theme.below.tablet`
      padding: 0px ${theme.newSpaces.xs2}px;
    `}

    ${theme.below.mobileXL`
      padding: ${padding !== undefined ? `${padding}px` : 'auto'};
    `}
  `}
`

export const Container = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-auto-rows: 1fr;
    grid-column-gap: ${theme.newSpaces.xs}px;
    grid-row-gap: ${theme.newSpaces.xs}px;
    grid-template-columns: repeat(2, 1fr);
    ${theme.below.mobileXL`
      grid-template-columns: repeat(1, 1fr);
    `}
    & img {
      z-index: 5;
    }
  `}
`

export const WrapperIcon = styled(Box)<IconProps>`
  ${({ $width }) => css`
    $width: 100%;
    max-width: ${$width}px;
  `}
`

export const Items = styled.div<ItemsProps>`
  ${({ theme, heightBlock = '100%', imageClassName, hover, padding }) => css`
    background-color: ${theme.colors.backLight};
    height: ${heightBlock};
    overflow: hidden;
    padding: ${theme.newSpaces.md}px ${padding || theme.newSpaces.xl3}px;
    position: relative;
    &:hover {
      background-color: ${theme.colors.cloud};
      .rotate {
        -webkit-transform: rotate(-90deg);
        transform: rotate(-90deg);
      }
      .wheel-rotate {
        -webkit-transform: rotate(10deg);
        transform: rotate(10deg);
      }
    }

    ${() =>
      hover
        ? `&:hover {
              ${imageClassName && svgClassesStyles(imageClassName)}
            }
          `
        : ``}

    ${theme.below.tablet`
      padding: ${theme.newSpaces.xs}px;
      padding-bottom ${theme.newSpaces.sm}px;
      height: 100%;
    `}
  `}
`

export const Title = styled(Heading)<TitleProps>`
  ${({ theme, maxWidth = '265px' }) => css`
    ${theme.getTypography('desktop', 'title', 'title2')}
    color: ${theme.newColors.primary};

    margin-left: ${theme.newSpaces.xs}px;
    max-width: ${maxWidth};
    min-width: ${maxWidth};
    white-space: pre-line;
    z-index: 2;

    ${theme.above.laptopL`
      max-width: 100%;
    `}

    ${theme.below.laptop`
      min-width: unset;
    `}

    ${theme.below.tablet`
      ${theme.getTypography('mobile', 'title', 'title3')}

      margin-left: ${theme.newSpaces.xs2}px;
    `}
   ${theme.below.mobileXL`
      ${theme.getTypography('mobile', 'title', 'title3')};

      text-transform: uppercase;
    `}
  `}
`

export const Subtitle = styled.p`
  ${({ theme }) => css`
    ${theme.getTypography('desktop', 'body', 'body2')}

    position: inherit;
    z-index: 2;

    ${theme.below.tablet`
      ${theme.getTypography('mobile', 'body', 'body3')}

      font-weight: 300;
    `}
  `}
`

export const Content = styled.div<ContentProps>`
  ${({ theme, isVisible, imageClassName }) => css`
    align-items: center;
    display: flex;
    margin-bottom: ${theme.newSpaces.xs}px;
    max-height: 60px;
    z-index: 2;

    .rotate {
      -webkit-transform: ${isVisible ? 'rotate(-90deg)' : 'initial'};
      -webkit-transition: -webkit-transform 0.8s ease-in-out;
      transform: ${isVisible ? 'rotate(-90deg)' : 'initial'};
      transition: transform 0.4s ease-in-out;
    }

    .wheel-rotate {
      -webkit-transform: ${isVisible ? 'rotate(10deg)' : 'initial'};
      -webkit-transition: -webkit-transform 0.8s ease-in-out;
      transform: ${isVisible ? 'rotate(10deg)' : 'initial'};
      transition: transform 0.1s ease-in-out;
    }
    ${isVisible
      ? `
        & {
          ${svgClassesStyles(imageClassName)}
        }
      `
      : ''}

    ${theme.below.tablet`
      align-items: center;
    `}
  `}
`

export const AdditionalImage = styled.div<AdditionalImageProp>`
  ${({ top, bottom, right, left }) => css`
    bottom: ${bottom === 0 || bottom ? `${bottom}px` : 'initial'};
    left: ${left === 0 || left ? `${left}px` : 'initial'};
    position: absolute;
    right: ${right === 0 || right ? `${right}px` : 'initial'};
    top: ${top === 0 || top ? `${top}px` : 'initial'};
    z-index: 1;
  `}
`

export const OrangeHexagonRightImage = styled(OrangeHexagon)`
  position: absolute;
  right: -23%;
  top: 17%;

  ${({ theme }) => theme.below.laptopM`
      display: none;
  `}
`

export const OrangeHexagonLeftImage = styled(OrangeHexagon)`
  bottom: -30%;
  left: -23%;
  position: absolute;
  transform: rotateZ(180deg);

  ${({ theme }) => theme.below.laptopM`
      display: none;
  `}
`

export const GrayHexagonImage = styled(GrayGradientHexagon)`
  position: absolute;
  right: -19%;
  top: 27%;

  ${({ theme }) => theme.below.laptopM`
      display: none;
  `}
`
